/**
 * Custom "LinkedIn-style" consultant card. Replaces LinkedIn's official badge
 * iframe (which can't be themed cross-origin and showed a white box in dark
 * mode). Rendered entirely by us from data the admin enters — a profile URL,
 * plus optional name / title / photo — so it matches the app's light & dark
 * themes and always looks clean.
 *
 * Props:
 *   profileUrl — LinkedIn profile URL (required to render; the button target)
 *   name       — display name (falls back to the handle from the URL)
 *   title      — headline / role (optional)
 *   photoUrl   — avatar image URL (optional; initials shown otherwise)
 *   vanity     — LinkedIn handle (optional, used for fallback name + @handle)
 *   label      — caption shown above the card
 */
import { Linkedin } from "lucide-react";

function prettifyHandle(handle) {
  if (!handle) return "";
  return handle
    .replace(/-\w{6,}$/, "") // strip trailing id hashes like -1a2b3c4d
    .split(/[-_.]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function initials(name) {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() || "").join("") || "in";
}

export default function LinkedInBadge({
  profileUrl,
  name,
  title,
  photoUrl,
  vanity,
  label = "Connect on LinkedIn",
}) {
  if (!profileUrl) return null;
  const displayName = (name || "").trim() || prettifyHandle(vanity) || "LinkedIn profile";

  return (
    <div className="flex flex-col items-center" data-testid="linkedin-badge">
      {label && (
        <p className="text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400 mb-3">{label}</p>
      )}

      <div className="w-full max-w-[260px] rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-hidden shadow-sm">
        {/* LinkedIn-blue banner */}
        <div className="relative h-14 bg-[#0a66c2]" />

        <div className="px-5 pb-5 -mt-9 flex flex-col items-center text-center">
          {/* Avatar */}
          <div className="h-[72px] w-[72px] rounded-full ring-4 ring-white dark:ring-slate-900 overflow-hidden bg-[#004182] flex items-center justify-center shadow relative z-10">
            {photoUrl ? (
              <img src={photoUrl} alt={displayName} className="h-full w-full object-cover" data-testid="linkedin-photo" />
            ) : (
              <span className="text-white text-xl font-semibold tracking-wide">{initials(displayName)}</span>
            )}
          </div>

          <p className="mt-3 font-semibold text-[15px] text-slate-900 dark:text-slate-50 leading-tight" data-testid="linkedin-name">
            {displayName}
          </p>
          {title && (
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400 leading-snug">{title}</p>
          )}

          <a
            href={profileUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="linkedin-view-profile"
            className="mt-4 inline-flex items-center gap-2 h-9 px-5 rounded-full border border-[#0a66c2] text-[#0a66c2] dark:text-sky-300 dark:border-sky-500/70 text-sm font-semibold hover:bg-[#0a66c2] hover:text-white dark:hover:bg-sky-500/20 transition-colors"
          >
            <Linkedin className="h-4 w-4" /> View profile
          </a>
        </div>
      </div>
    </div>
  );
}
